/**
 * Sidebar Component
 * Task 5.6: Sidebar
 *
 * Collapsible panel displaying all comments for the current branch.
 * Uses PDS design tokens for consistent Pantheon branding.
 *
 * Features:
 * - Comments grouped by page path
 * - Current page group highlighted and shown first
 * - Status filtering (all, open, resolved)
 * - Click to navigate and highlight element
 * - Orphaned comments section with re-anchor action
 * - Refresh button for manual sync
 * - "New comments available" indicator
 * - Full accessibility support
 *
 * Architecture:
 * - Receives all data via props
 * - Internal state for filter selection only
 * - Actions delegated to parent via callbacks
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import type { Comment, TeamMember } from '../../lib/types';
import { tokens } from '../../styles/tokens';
import { MoreVerticalIcon, CheckIcon } from '../icons/Icons';

/** Sidebar width constant - used for both sidebar and viewport push */
const SIDEBAR_WIDTH = tokens.layout.sidebarWidth;

export type StatusFilter = 'all' | 'open' | 'resolved';

const STORAGE_KEY = 'multidev-sidebar-filter';

/**
 * Read filter from localStorage with fallback
 */
function getStoredFilter(): StatusFilter {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'all' || stored === 'open' || stored === 'resolved') {
      return stored;
    }
  } catch {
    // localStorage may not be available
  }
  return 'all';
}

/**
 * Write filter to localStorage
 */
function setStoredFilter(filter: StatusFilter): void {
  try {
    localStorage.setItem(STORAGE_KEY, filter);
  } catch {
    // localStorage may not be available
  }
}

export interface SidebarProps {
  comments: Comment[];
  authors: Map<string, TeamMember>;
  currentPagePath: string;
  isOpen: boolean;
  selectedCommentId?: string;
  /** Comment ID to highlight (e.g., from deep link sidebar fallback) */
  highlightedCommentId?: string;
  orphanedCommentIds?: string[];
  /** Set of parent comment IDs that are unread for the current user */
  unreadCommentIds?: Set<string>;
  hasNewComments?: boolean;
  isLoading?: boolean;
  onToggle?: () => void;
  onCommentClick?: (comment: Comment) => void;
  onReAnchor?: (comment: Comment) => void;
  onResolve?: (comment: Comment) => void;
  onMarkAsRead?: (comment: Comment) => void;
  onMarkAsUnread?: (comment: Comment) => void;
  onCopyLink?: (comment: Comment) => void;
  onRefresh?: () => void;
}

/**
 * Group comments by page path, with current page first
 */
function groupCommentsByPage(
  comments: Comment[],
  currentPagePath: string
): Map<string, Comment[]> {
  const groups = new Map<string, Comment[]>();

  // Group all comments
  comments.forEach((comment) => {
    const existing = groups.get(comment.pagePath) || [];
    existing.push(comment);
    groups.set(comment.pagePath, existing);
  });

  // Sort to put current page first
  const sortedGroups = new Map<string, Comment[]>();

  // Add current page first if it exists
  if (groups.has(currentPagePath)) {
    sortedGroups.set(currentPagePath, groups.get(currentPagePath)!);
    groups.delete(currentPagePath);
  }

  // Add remaining pages sorted alphabetically
  const remainingPages = Array.from(groups.keys()).sort();
  remainingPages.forEach((page) => {
    sortedGroups.set(page, groups.get(page)!);
  });

  return sortedGroups;
}

/**
 * Filter button component
 */
const FilterButton: React.FC<{
  label: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ label, isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isActive}
    style={{
      padding: `${tokens.spacing['5xs']} ${tokens.spacing.s}`,
      fontSize: '12px',
      fontWeight: isActive ? tokens.typography.fontWeight.semibold : tokens.typography.fontWeight.regular,
      color: isActive ? tokens.colors.textReverse : tokens.colors.textSecondary,
      backgroundColor: isActive ? tokens.colors.primary : 'transparent',
      border: '1px solid',
      borderColor: isActive ? tokens.colors.primary : tokens.colors.borderDefault,
      borderRadius: '16px',
      cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

/**
 * Individual comment item in sidebar
 */
const SidebarCommentItem: React.FC<{
  comment: Comment;
  author: TeamMember;
  isSelected: boolean;
  isHighlighted: boolean;
  isOrphaned: boolean;
  isUnread?: boolean;
  replyCount: number;
  onClick?: () => void;
  onReAnchor?: () => void;
  onResolve?: () => void;
  onMarkAsRead?: () => void;
  onMarkAsUnread?: () => void;
  onCopyLink?: () => void;
  itemRef?: React.Ref<HTMLDivElement>;
}> = ({ comment, author, isSelected, isHighlighted, isOrphaned, isUnread, replyCount, onClick, onReAnchor, onResolve, onMarkAsRead, onMarkAsUnread, onCopyLink, itemRef }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu on click outside or ESC key
  React.useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMenu]);

  const handleResolveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onResolve?.();
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleMarkAsReadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO [011526]: Remove debug logging after fixing sidebar menu actions
    console.log('[011526] Sidebar: handleMarkAsReadClick called for comment:', comment.id);
    onMarkAsRead?.();
    setShowMenu(false);
  };

  const handleMarkAsUnreadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO [011526]: Remove debug logging after fixing sidebar menu actions
    console.log('[011526] Sidebar: handleMarkAsUnreadClick called for comment:', comment.id);
    onMarkAsUnread?.();
    setShowMenu(false);
  };

  const handleCopyLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO [011526]: Remove debug logging after fixing sidebar menu actions
    console.log('[011526] Sidebar: handleCopyLinkClick called for comment:', comment.id);
    onCopyLink?.();
    setShowMenu(false);
  };

  // Determine background color based on state priority: highlighted > selected > orphaned > default
  const getBackgroundColor = () => {
    if (isHighlighted) return tokens.colors.infoBg;
    if (isSelected) return tokens.colors.primaryLight;
    if (isOrphaned) return tokens.colors.warningBg;
    return tokens.colors.bgDefault;
  };

  // Determine border color based on state priority
  const getBorderColor = () => {
    if (isHighlighted) return tokens.colors.info;
    if (isSelected) return tokens.colors.primary;
    return tokens.colors.borderDefault;
  };

  return (
  <div
    ref={itemRef}
    data-testid={`sidebar-comment-${comment.id}`}
    data-selected={isSelected ? 'true' : 'false'}
    data-highlighted={isHighlighted ? 'true' : 'false'}
    data-orphaned={isOrphaned ? 'true' : 'false'}
    data-unread={isUnread ? 'true' : 'false'}
    onClick={onClick}
    onMouseEnter={() => setIsHovered(true)}
    onMouseLeave={() => {
      setIsHovered(false);
      setShowMenu(false);
    }}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    }}
    style={{
      position: 'relative',
      padding: tokens.spacing.s,
      backgroundColor: getBackgroundColor(),
      borderRadius: tokens.borders.radiusContainer,
      cursor: 'pointer',
      border: `1px solid ${getBorderColor()}`,
      boxShadow: isHighlighted ? `0 0 0 2px ${tokens.colors.info}40` : undefined,
    }}
  >
    {/* Unread indicator dot - hidden on hover */}
    {isUnread && !isHovered && (
      <span
        aria-label="Unread"
        style={{
          position: 'absolute',
          top: tokens.spacing['2xs'],
          right: tokens.spacing['2xs'],
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: tokens.colors.critical,
        }}
      />
    )}

    {/* Hover actions - resolve button and hamburger menu (desktop only) */}
    {isHovered && !isOrphaned && (
      <div
        style={{
          position: 'absolute',
          top: tokens.spacing['2xs'],
          right: tokens.spacing['2xs'],
          display: 'flex',
          gap: tokens.spacing['5xs'],
          alignItems: 'center',
        }}
      >
        {/* Resolve button */}
        {comment.status === 'open' && onResolve && (
          <button
            type="button"
            onClick={handleResolveClick}
            aria-label="Resolve comment"
            title="Resolve"
            style={{
              padding: '4px',
              background: tokens.colors.bgDefault,
              border: `1px solid ${tokens.colors.borderDefault}`,
              borderRadius: tokens.borders.radiusButton,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colors.gray100;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colors.bgDefault;
            }}
          >
            <CheckIcon size={16} color={tokens.colors.gray400} />
          </button>
        )}

        {/* Hamburger menu button */}
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            type="button"
            onClick={handleMenuClick}
            aria-label="More actions"
            aria-expanded={showMenu}
            title="More actions"
            style={{
              padding: '4px',
              background: tokens.colors.bgDefault,
              border: `1px solid ${tokens.colors.borderDefault}`,
              borderRadius: tokens.borders.radiusButton,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colors.gray100;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colors.bgDefault;
            }}
          >
            <MoreVerticalIcon size={16} color={tokens.colors.gray400} />
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <div
              data-testid="sidebar-menu"
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                backgroundColor: tokens.colors.bgDefault,
                border: `${tokens.borders.widthDefault} solid ${tokens.colors.borderDefault}`,
                borderRadius: tokens.borders.radiusContainer,
                boxShadow: tokens.elevation.overlay,
                zIndex: tokens.zIndex.modal,
                minWidth: '160px',
              }}
            >
              {/* Mark as read/unread option */}
              {isUnread && onMarkAsRead && (
                <button
                  type="button"
                  onClick={handleMarkAsReadClick}
                  onMouseDown={(e) => {
                    // TODO [011526]: Remove this fix after confirming it resolves the click issue
                    e.stopPropagation(); // Prevent click-outside from closing menu before onClick fires
                  }}
                  data-testid="sidebar-mark-as-read-button"
                  style={{
                    width: '100%',
                    padding: `${tokens.spacing['2xs']} ${tokens.spacing.s}`,
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: tokens.typography.fontSize.s,
                    color: tokens.colors.textDefault,
                    display: 'block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = tokens.colors.gray100;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Mark as read
                </button>
              )}
              {!isUnread && onMarkAsUnread && (
                <button
                  type="button"
                  onClick={handleMarkAsUnreadClick}
                  onMouseDown={(e) => {
                    // TODO [011526]: Remove this fix after confirming it resolves the click issue
                    e.stopPropagation(); // Prevent click-outside from closing menu before onClick fires
                  }}
                  data-testid="sidebar-mark-as-unread-button"
                  style={{
                    width: '100%',
                    padding: `${tokens.spacing['2xs']} ${tokens.spacing.s}`,
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: tokens.typography.fontSize.s,
                    color: tokens.colors.textDefault,
                    display: 'block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = tokens.colors.gray100;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Mark as unread
                </button>
              )}

              {/* Copy link option */}
              {onCopyLink && (
                <button
                  type="button"
                  onClick={handleCopyLinkClick}
                  onMouseDown={(e) => {
                    // TODO [011526]: Remove this fix after confirming it resolves the click issue
                    e.stopPropagation(); // Prevent click-outside from closing menu before onClick fires
                  }}
                  data-testid="sidebar-copy-link-button"
                  style={{
                    width: '100%',
                    padding: `${tokens.spacing['2xs']} ${tokens.spacing.s}`,
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: tokens.typography.fontSize.s,
                    color: tokens.colors.textDefault,
                    display: 'block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = tokens.colors.gray100;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Copy link
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )}
    {/* Author and time */}
    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing['2xs'], marginBottom: tokens.spacing['5xs'] }}>
      {author.avatar ? (
        <img
          src={author.avatar}
          alt=""
          style={{ width: '20px', height: '20px', borderRadius: '50%' }}
        />
      ) : (
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: tokens.colors.primary,
            color: tokens.colors.textReverse,
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {author.name[0]}
        </div>
      )}
      <span style={{ fontSize: '12px', fontWeight: tokens.typography.fontWeight.medium, color: tokens.colors.textDefault }}>
        {author.name}
      </span>
      <span style={{ fontSize: '11px', color: tokens.colors.textSecondary }}>
        {comment.status === 'resolved' && '✓ '}
      </span>
    </div>

    {/* Content preview */}
    <p
      style={{
        margin: 0,
        fontSize: '13px',
        color: tokens.colors.textDefault,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {comment.content}
    </p>

    {/* Reply count indicator */}
    {replyCount > 0 && (
      <p
        style={{
          margin: `${tokens.spacing['5xs']} 0 0 0`,
          fontSize: '11px',
          color: tokens.colors.primary,
          fontWeight: tokens.typography.fontWeight.medium,
        }}
      >
        {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
      </p>
    )}

    {/* Re-anchor button for orphaned comments */}
    {isOrphaned && onReAnchor && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onReAnchor();
        }}
        aria-label="Re-anchor"
        style={{
          marginTop: tokens.spacing['2xs'],
          padding: `${tokens.spacing['5xs']} ${tokens.spacing['2xs']}`,
          fontSize: '11px',
          color: tokens.colors.warningFg,
          backgroundColor: tokens.colors.warningBg,
          border: `1px solid ${tokens.colors.warning}`,
          borderRadius: tokens.borders.radiusButton,
          cursor: 'pointer',
        }}
      >
        Re-anchor
      </button>
    )}
  </div>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  comments,
  authors,
  currentPagePath,
  isOpen,
  selectedCommentId,
  highlightedCommentId,
  orphanedCommentIds = [],
  unreadCommentIds,
  hasNewComments = false,
  isLoading = false,
  onToggle,
  onCommentClick,
  onReAnchor,
  onResolve,
  onMarkAsRead,
  onMarkAsUnread,
  onCopyLink,
  onRefresh,
}) => {
  // Initialize filter from localStorage
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(getStoredFilter);

  // Track the last highlighted ID to avoid re-scrolling on every render
  const lastScrolledIdRef = useRef<string | null>(null);

  // Callback ref that scrolls to the highlighted comment when mounted
  const highlightedCommentRef = useCallback((node: HTMLDivElement | null) => {
    if (node && highlightedCommentId && isOpen && lastScrolledIdRef.current !== highlightedCommentId) {
      lastScrolledIdRef.current = highlightedCommentId;
      // Small delay to ensure sidebar is fully open
      setTimeout(() => {
        node.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [highlightedCommentId, isOpen]);

  // Handle filter change with persistence
  const handleFilterChange = useCallback((filter: StatusFilter) => {
    setStatusFilter(filter);
    setStoredFilter(filter);
  }, []);

  // Get author with fallback - prefer comment.author (from API) over authors map
  const getAuthor = (comment: Comment): TeamMember => {
    // Use the author field directly from the comment if available (populated by API JOIN)
    if (comment.author) {
      return {
        id: comment.author.id,
        name: comment.author.name || 'Unknown',
        email: comment.author.email || '',
        avatar: comment.author.picture || null,
      };
    }
    // Fall back to authors map lookup
    return authors.get(comment.authorId) || {
      id: comment.authorId,
      name: 'Unknown',
      email: '',
      avatar: null,
    };
  };

  // Filter comments by status, excluding replies (only show parent comments)
  const filteredComments = useMemo(() => {
    // First filter to only parent comments (replies are visible in thread view)
    const parentComments = comments.filter((c) => c.threadId === null);

    if (statusFilter === 'all') {
      return parentComments;
    }
    return parentComments.filter((c) => c.status === statusFilter);
  }, [comments, statusFilter]);

  // Separate orphaned and anchored comments
  const orphanedSet = useMemo(() => new Set(orphanedCommentIds), [orphanedCommentIds]);

  const orphanedComments = useMemo(
    () => filteredComments.filter((c) => orphanedSet.has(c.id)),
    [filteredComments, orphanedSet]
  );

  const anchoredComments = useMemo(
    () => filteredComments.filter((c) => !orphanedSet.has(c.id)),
    [filteredComments, orphanedSet]
  );

  // Group anchored comments by page
  const pageGroups = useMemo(
    () => groupCommentsByPage(anchoredComments, currentPagePath),
    [anchoredComments, currentPagePath]
  );

  // Build reply count map (parent comment id -> number of replies)
  const replyCountMap = useMemo(() => {
    const map = new Map<string, number>();
    comments.forEach((c) => {
      if (c.threadId) {
        map.set(c.threadId, (map.get(c.threadId) || 0) + 1);
      }
    });
    return map;
  }, [comments]);

  // Count only parent comments (not replies)
  const parentCommentCount = comments.filter((c) => c.threadId === null).length;

  return (
    <aside
      data-testid="sidebar-panel"
      data-multidev-sdk="true"
      data-open={isOpen ? 'true' : 'false'}
      role="complementary"
      aria-label="Comments sidebar"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: isOpen ? SIDEBAR_WIDTH : '0',
        height: '100vh',
        backgroundColor: tokens.colors.bgSecondary,
        borderLeft: isOpen ? `1px solid ${tokens.colors.borderDefault}` : 'none',
        overflow: 'hidden',
        transition: `width ${tokens.animation.duration} ${tokens.animation.timing}`,
        zIndex: 999998,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: tokens.spacing.m,
          borderBottom: `1px solid ${tokens.colors.borderDefault}`,
          backgroundColor: tokens.colors.bgDefault,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: tokens.typography.fontWeight.semibold, color: tokens.colors.textDefault }}>
              Comments
            </h2>
            <p style={{ margin: `${tokens.spacing['5xs']} 0 0`, fontSize: '12px', color: tokens.colors.textSecondary }}>
              {parentCommentCount} {parentCommentCount === 1 ? 'comment' : 'comments'} · {currentPagePath}
            </p>
          </div>

          <div style={{ display: 'flex', gap: tokens.spacing['2xs'] }}>
            {/* Refresh button */}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                aria-label="Refresh"
                style={{
                  padding: '6px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: tokens.colors.textSecondary,
                }}
              >
                ↻
              </button>
            )}

            {/* Close button */}
            {onToggle && (
              <button
                type="button"
                onClick={onToggle}
                aria-label="Close"
                style={{
                  padding: '6px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: tokens.colors.textSecondary,
                  fontSize: '16px',
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* New comments indicator */}
        {hasNewComments && (
          <div
            style={{
              marginTop: tokens.spacing['2xs'],
              padding: `${tokens.spacing['2xs']} ${tokens.spacing.s}`,
              backgroundColor: tokens.colors.infoBg,
              borderRadius: tokens.borders.radiusContainer,
              fontSize: '12px',
              color: tokens.colors.infoFg,
            }}
          >
            New comments available
          </div>
        )}

        {/* Filter buttons */}
        <div style={{ display: 'flex', gap: tokens.spacing['2xs'], marginTop: tokens.spacing.s }}>
          <FilterButton
            label="All"
            isActive={statusFilter === 'all'}
            onClick={() => handleFilterChange('all')}
          />
          <FilterButton
            label="Open"
            isActive={statusFilter === 'open'}
            onClick={() => handleFilterChange('open')}
          />
          <FilterButton
            label="Resolved"
            isActive={statusFilter === 'resolved'}
            onClick={() => handleFilterChange('resolved')}
          />
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: tokens.spacing.m,
        }}
      >
        {/* Loading state */}
        {isLoading && (
          <div
            role="status"
            aria-label="Loading comments"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                border: `2px solid ${tokens.colors.primary}`,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && filteredComments.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: tokens.colors.textSecondary,
            }}
          >
            <p style={{ margin: 0, fontSize: '14px' }}>No comments yet</p>
            <p style={{ margin: `${tokens.spacing['2xs']} 0 0`, fontSize: '12px' }}>
              Click on any element to add a comment
            </p>
          </div>
        )}

        {/* Orphaned comments section */}
        {orphanedComments.length > 0 && (
          <div style={{ marginBottom: tokens.spacing.m }}>
            <h3
              style={{
                margin: `0 0 ${tokens.spacing['2xs']} 0`,
                fontSize: '12px',
                fontWeight: tokens.typography.fontWeight.semibold,
                color: tokens.colors.warningFg,
                textTransform: 'uppercase',
              }}
            >
              Orphaned ({orphanedComments.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing['2xs'] }}>
              {orphanedComments.map((comment) => (
                <SidebarCommentItem
                  key={comment.id}
                  comment={comment}
                  author={getAuthor(comment)}
                  isSelected={comment.id === selectedCommentId}
                  isHighlighted={comment.id === highlightedCommentId}
                  isOrphaned={true}
                  isUnread={unreadCommentIds?.has(comment.id)}
                  replyCount={replyCountMap.get(comment.id) || 0}
                  onClick={() => onCommentClick?.(comment)}
                  onReAnchor={() => onReAnchor?.(comment)}
                  onResolve={() => onResolve?.(comment)}
                  onMarkAsRead={() => onMarkAsRead?.(comment)}
                  onMarkAsUnread={() => onMarkAsUnread?.(comment)}
                  onCopyLink={() => onCopyLink?.(comment)}
                  itemRef={comment.id === highlightedCommentId ? highlightedCommentRef : undefined}
                />
              ))}
            </div>
          </div>
        )}

        {/* Page groups */}
        {Array.from(pageGroups.entries()).map(([pagePath, pageComments]) => (
          <div
            key={pagePath}
            data-testid={`page-group-${pagePath}`}
            data-current-page={pagePath === currentPagePath ? 'true' : 'false'}
            style={{ marginBottom: tokens.spacing.m }}
          >
            <h3
              style={{
                margin: `0 0 ${tokens.spacing['2xs']} 0`,
                fontSize: '12px',
                fontWeight: tokens.typography.fontWeight.semibold,
                color: pagePath === currentPagePath ? tokens.colors.primary : tokens.colors.textSecondary,
              }}
            >
              {pagePath}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing['2xs'] }}>
              {pageComments.map((comment) => (
                <SidebarCommentItem
                  key={comment.id}
                  comment={comment}
                  author={getAuthor(comment)}
                  isSelected={comment.id === selectedCommentId}
                  isHighlighted={comment.id === highlightedCommentId}
                  isOrphaned={false}
                  isUnread={unreadCommentIds?.has(comment.id)}
                  replyCount={replyCountMap.get(comment.id) || 0}
                  onClick={() => onCommentClick?.(comment)}
                  onResolve={() => onResolve?.(comment)}
                  onMarkAsRead={() => onMarkAsRead?.(comment)}
                  onMarkAsUnread={() => onMarkAsUnread?.(comment)}
                  onCopyLink={() => onCopyLink?.(comment)}
                  itemRef={comment.id === highlightedCommentId ? highlightedCommentRef : undefined}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};
